import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import axios from 'axios';
import { Plus, Briefcase, Calendar, DollarSign, Trash2, Edit, CheckCircle, Wallet, User } from 'lucide-react';
import { EmployeeAdvances } from '../components/EmployeeAdvances';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_TYPES = [
  { key: 'salario', label: 'Salário' },
  { key: 'inss', label: 'INSS' },
  { key: 'fgts', label: 'FGTS' },
  { key: 'ferias', label: 'Férias' },
  { key: '13o', label: '13º' },
  { key: 'rescisao', label: 'Rescisão' },
  { key: 'outro', label: 'Outro' },
];

const todayStr = () => new Date().toISOString().substring(0, 10);

export const EmployeesPage = () => {
  const [tab, setTab] = useState('list');
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formTab, setFormTab] = useState('dados');
  const [pendingAdv, setPendingAdv] = useState(null);
  const [empForm, setEmpForm] = useState({
    name: '', role: '', cpf: '', phone: '', address: '',
    employment_type: 'clt',
    admission_date: '', dismissal_date: '',
    salary: '', inss_percent: '8', fgts_percent: '8', daily_rate: '',
    pix_key: '', notes: '', active: true
  });
  const [payForm, setPayForm] = useState({ employee_id: '', payment_type: 'salario', reference_month: '', amount: '', notes: '' });
  const [shiftForm, setShiftForm] = useState({ employee_id: '', employee_name: '', date: todayStr(), role: '', shift: 'integral', daily_rate: '', paid: false, notes: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const [e, p, s] = await Promise.all([
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/employees/payments`, { withCredentials: true }),
        axios.get(`${API}/employees/shifts`, { withCredentials: true })
      ]);
      setEmployees(e.data); setPayments(p.data); setShifts(s.data);
    } catch (err) { console.error(err); }
  };

  const handleEmpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const payload = { ...empForm };
      payload.salary = parseFloat(empForm.salary) || 0;
      payload.inss_percent = parseFloat(empForm.inss_percent) || 0;
      payload.fgts_percent = parseFloat(empForm.fgts_percent) || 0;
      payload.daily_rate = parseFloat(empForm.daily_rate) || 0;
      payload.admission_date = empForm.admission_date ? new Date(empForm.admission_date).toISOString() : null;
      payload.dismissal_date = empForm.dismissal_date ? new Date(empForm.dismissal_date).toISOString() : null;
      if (editingId) {
        await axios.put(`${API}/employees/${editingId}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/employees`, payload, { withCredentials: true });
      }
      setSuccess('Funcionário salvo');
      setShowForm(false); setEditingId(null);
      setEmpForm({ name: '', role: '', cpf: '', phone: '', address: '', employment_type: 'clt', admission_date: '', dismissal_date: '', salary: '', inss_percent: '8', fgts_percent: '8', daily_rate: '', pix_key: '', notes: '', active: true });
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setLoading(false); }
  };

  const handleEdit = (emp, tabName = 'dados') => {
    setFormTab(tabName);
    setEmpForm({
      name: emp.name || '', role: emp.role || '', cpf: emp.cpf || '', phone: emp.phone || '', address: emp.address || '',
      employment_type: emp.employment_type || 'clt',
      admission_date: emp.admission_date ? emp.admission_date.substring(0, 10) : '',
      dismissal_date: emp.dismissal_date ? emp.dismissal_date.substring(0, 10) : '',
      salary: String(emp.salary || ''), inss_percent: String(emp.inss_percent || ''), fgts_percent: String(emp.fgts_percent || ''), daily_rate: String(emp.daily_rate || ''),
      pix_key: emp.pix_key || '', notes: emp.notes || '', active: emp.active !== false
    });
    setEditingId(emp.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir funcionário?')) return;
    await axios.delete(`${API}/employees/${id}`, { withCredentials: true });
    load();
  };

  useEffect(() => {
    if (payForm.employee_id && payForm.payment_type === 'salario') {
      axios.get(`${API}/employees/advances/pending-summary`, { params: { employee_id: payForm.employee_id }, withCredentials: true })
        .then((r) => setPendingAdv(r.data)).catch(() => setPendingAdv(null));
    } else setPendingAdv(null);
  }, [payForm.employee_id, payForm.payment_type]);

  const handlePaySubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/employees/payments`, {
        employee_id: payForm.employee_id,
        payment_type: payForm.payment_type,
        reference_month: payForm.reference_month || new Date().toISOString().substring(0, 7),
        amount: parseFloat(payForm.amount),
        paid: true,
        notes: payForm.notes
      }, { withCredentials: true });
      setSuccess(pendingAdv?.total > 0 ? `Pagamento registrado — R$ ${pendingAdv.total.toFixed(2)} em vales descontados` : 'Pagamento registrado');
      setPayForm({ employee_id: '', payment_type: 'salario', reference_month: '', amount: '', notes: '' });
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const handleShiftSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      const emp = employees.find(x => x.id === shiftForm.employee_id);
      const payload = {
        employee_id: shiftForm.employee_id || null,
        employee_name: emp ? emp.name : shiftForm.employee_name,
        date: new Date(shiftForm.date).toISOString(),
        role: shiftForm.role || (emp?.role || null),
        shift: shiftForm.shift,
        daily_rate: parseFloat(shiftForm.daily_rate) || 0,
        paid: shiftForm.paid,
        notes: shiftForm.notes || null
      };
      if (!payload.employee_name) { setError('Informe o nome do funcionário'); setLoading(false); return; }
      await axios.post(`${API}/employees/shifts`, payload, { withCredentials: true });
      setSuccess('Diária registrada');
      setShiftForm({ employee_id: '', employee_name: '', date: todayStr(), role: '', shift: 'integral', daily_rate: '', paid: false, notes: '' });
      load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.detail || 'Erro'); }
    finally { setLoading(false); }
  };

  const markShiftPaid = async (id) => {
    await axios.post(`${API}/employees/shifts/${id}/mark-paid`, {}, { withCredentials: true });
    load();
  };

  return (
    <Layout>
      <div className="p-8" data-testid="employees-page">
        <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-6">Funcionários</h1>

        {success && <Alert className="border-green-600 bg-green-50 mb-4"><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>}
        {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full md:w-[600px] grid-cols-3 mb-6">
            <TabsTrigger value="list" data-testid="emp-tab-list"><Briefcase size={16} className="mr-2" /> Funcionários</TabsTrigger>
            <TabsTrigger value="payments" data-testid="emp-tab-payments"><DollarSign size={16} className="mr-2" /> Pagamentos</TabsTrigger>
            <TabsTrigger value="shifts" data-testid="emp-tab-shifts"><Calendar size={16} className="mr-2" /> Diárias</TabsTrigger>
          </TabsList>

          {/* LISTA / FORM */}
          <TabsContent value="list">
            <div className="flex justify-end mb-4">
              <Button onClick={() => { setShowForm(!showForm); setEditingId(null); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-emp-btn">
                <Plus size={18} className="mr-2" /> Novo Funcionário
              </Button>
            </div>
            {showForm && (
              <div className="bg-white rounded-md border border-slate-200 p-6 mb-6">
                <h2 className="text-2xl font-bold mb-3">{editingId ? 'Editar' : 'Cadastrar'} Funcionário</h2>
                {editingId && (
                  <div className="flex gap-1 mb-4 bg-slate-100 rounded-md p-1 w-fit" data-testid="emp-form-tabs">
                    <button type="button" onClick={() => setFormTab('dados')} className={`px-4 py-1.5 rounded text-sm font-bold flex items-center gap-1.5 ${formTab === 'dados' ? 'bg-white shadow text-blue-700' : 'text-slate-600'}`} data-testid="emp-form-tab-dados"><User size={14} /> Dados</button>
                    <button type="button" onClick={() => setFormTab('vales')} className={`px-4 py-1.5 rounded text-sm font-bold flex items-center gap-1.5 ${formTab === 'vales' ? 'bg-white shadow text-blue-700' : 'text-slate-600'}`} data-testid="emp-form-tab-vales"><Wallet size={14} /> Vales</button>
                  </div>
                )}
                {editingId && formTab === 'vales' ? (
                  <EmployeeAdvances employee={employees.find((x) => x.id === editingId) || { id: editingId, name: empForm.name }} onChanged={load} />
                ) : (
                <form onSubmit={handleEmpSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1"><Label>Nome *</Label><Input value={empForm.name} onChange={(e) => setEmpForm({ ...empForm, name: e.target.value })} required data-testid="emp-name" /></div>
                  <div className="space-y-1"><Label>Função/Cargo</Label><Input value={empForm.role} onChange={(e) => setEmpForm({ ...empForm, role: e.target.value })} placeholder="Ex: Açougueiro" /></div>
                  <div className="space-y-1"><Label>Tipo</Label>
                    <Select value={empForm.employment_type} onValueChange={(v) => setEmpForm({ ...empForm, employment_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clt">CLT</SelectItem>
                        <SelectItem value="diarista">Diarista</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>CPF</Label><Input value={empForm.cpf} onChange={(e) => setEmpForm({ ...empForm, cpf: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Telefone</Label><Input value={empForm.phone} onChange={(e) => setEmpForm({ ...empForm, phone: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Chave PIX</Label><Input value={empForm.pix_key} onChange={(e) => setEmpForm({ ...empForm, pix_key: e.target.value })} /></div>
                  <div className="space-y-1 md:col-span-3"><Label>Endereço</Label><Input value={empForm.address} onChange={(e) => setEmpForm({ ...empForm, address: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Admissão</Label><Input type="date" value={empForm.admission_date} onChange={(e) => setEmpForm({ ...empForm, admission_date: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Demissão</Label><Input type="date" value={empForm.dismissal_date} onChange={(e) => setEmpForm({ ...empForm, dismissal_date: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Salário (R$)</Label><Input type="number" step="0.01" value={empForm.salary} onChange={(e) => setEmpForm({ ...empForm, salary: e.target.value })} /></div>
                  <div className="space-y-1"><Label>INSS (%)</Label><Input type="number" step="0.01" value={empForm.inss_percent} onChange={(e) => setEmpForm({ ...empForm, inss_percent: e.target.value })} /></div>
                  <div className="space-y-1"><Label>FGTS (%)</Label><Input type="number" step="0.01" value={empForm.fgts_percent} onChange={(e) => setEmpForm({ ...empForm, fgts_percent: e.target.value })} /></div>
                  <div className="space-y-1"><Label>Diária (R$)</Label><Input type="number" step="0.01" value={empForm.daily_rate} onChange={(e) => setEmpForm({ ...empForm, daily_rate: e.target.value })} /></div>
                  <div className="md:col-span-3 flex gap-2 pt-2">
                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">{loading ? 'Salvando...' : 'Salvar'}</Button>
                    <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancelar</Button>
                  </div>
                </form>
                )}
              </div>
            )}

            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Nome</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Função</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Admissão</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Salário</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Diária</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Status</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {employees.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400">Nenhum funcionário cadastrado</td></tr>}
                  {employees.map(emp => (
                    <tr key={emp.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{emp.name}</td>
                      <td className="px-4 py-3 text-sm">{emp.role || '-'}</td>
                      <td className="px-4 py-3 text-sm capitalize">{emp.employment_type}</td>
                      <td className="px-4 py-3 text-sm">{emp.admission_date ? new Date(emp.admission_date).toLocaleDateString('pt-BR') : '-'}</td>
                      <td className="px-4 py-3 text-right">R$ {Number(emp.salary || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">R$ {Number(emp.daily_rate || 0).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${emp.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {emp.active ? 'Ativo' : 'Demitido'}
                        </span>
                      </td>
                      <td className="px-4 py-3 flex gap-2">
                        <button onClick={() => handleEdit(emp)} className="text-blue-600" title="Editar" data-testid={`emp-edit-${emp.id}`}><Edit size={16} /></button>
                        <button onClick={() => handleEdit(emp, 'vales')} className="text-amber-600" title="Vales / adiantamentos" data-testid={`emp-advances-${emp.id}`}><Wallet size={16} /></button>
                        <button onClick={() => handleDelete(emp.id)} className="text-red-600"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* PAGAMENTOS */}
          <TabsContent value="payments">
            <div className="bg-white rounded-md border border-slate-200 p-4 mb-4">
              <h3 className="font-bold mb-3">Registrar Pagamento</h3>
              <form onSubmit={handlePaySubmit} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div className="space-y-1 md:col-span-2"><Label>Funcionário *</Label>
                  <select required value={payForm.employee_id} onChange={(e) => setPayForm({ ...payForm, employee_id: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded">
                    <option value="">Selecione...</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Tipo</Label>
                  <select value={payForm.payment_type} onChange={(e) => setPayForm({ ...payForm, payment_type: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded">
                    {PAYMENT_TYPES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Mês ref. (YYYY-MM) *</Label>
                  <Input value={payForm.reference_month} onChange={(e) => setPayForm({ ...payForm, reference_month: e.target.value })} placeholder="2026-05" required />
                </div>
                <div className="space-y-1"><Label>Valor (R$) *</Label>
                  <Input type="number" step="0.01" required value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
                </div>
                {pendingAdv?.total > 0 && (
                  <div className="md:col-span-5 bg-amber-50 border border-amber-300 rounded-md p-3 text-sm" data-testid="salary-advances-preview">
                    <b>{pendingAdv.count} vale(s) pendente(s): R$ {pendingAdv.total.toFixed(2)}</b> — serão descontados automaticamente.
                    {parseFloat(payForm.amount) > 0 && <span className="ml-2">Salário R$ {parseFloat(payForm.amount).toFixed(2)} − vales R$ {pendingAdv.total.toFixed(2)} = <b>líquido a pagar R$ {(parseFloat(payForm.amount) - pendingAdv.total).toFixed(2)}</b></span>}
                  </div>
                )}
                <div className="md:col-span-5">
                  <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="pay-submit-btn">Registrar como pago</Button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Data</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Funcionário</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Mês ref.</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Valor</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Vales</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Líquido</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {payments.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Nenhum pagamento</td></tr>}
                  {payments.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">{new Date(p.created_at).toLocaleDateString('pt-BR')}</td>
                      <td className="px-4 py-3">{p.employee_name}</td>
                      <td className="px-4 py-3 capitalize">{p.payment_type}</td>
                      <td className="px-4 py-3">{p.reference_month}</td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">R$ {p.amount.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-amber-700">{p.advances_deducted > 0 ? `− R$ ${p.advances_deducted.toFixed(2)}` : '-'}</td>
                      <td className="px-4 py-3 text-right font-bold">R$ {Number(p.net_amount ?? p.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* DIÁRIAS */}
          <TabsContent value="shifts">
            <div className="bg-white rounded-md border border-slate-200 p-4 mb-4">
              <h3 className="font-bold mb-3">Registrar Diária</h3>
              <form onSubmit={handleShiftSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
                <div className="space-y-1 md:col-span-2"><Label>Funcionário</Label>
                  <select value={shiftForm.employee_id} onChange={(e) => {
                    const emp = employees.find(x => x.id === e.target.value);
                    setShiftForm({
                      ...shiftForm,
                      employee_id: e.target.value,
                      employee_name: emp?.name || '',
                      role: emp?.role || shiftForm.role,
                      daily_rate: emp?.daily_rate ? String(emp.daily_rate) : shiftForm.daily_rate
                    });
                  }} className="w-full h-10 px-3 border border-slate-200 rounded" data-testid="shift-emp-select">
                    <option value="">-- Selecione ou digite nome abaixo --</option>
                    {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
                <div className="space-y-1"><Label>Ou nome avulso</Label>
                  <Input value={shiftForm.employee_name} onChange={(e) => setShiftForm({ ...shiftForm, employee_name: e.target.value })} placeholder="Diarista não cadastrado" />
                </div>
                <div className="space-y-1"><Label>Função</Label>
                  <Input value={shiftForm.role} onChange={(e) => setShiftForm({ ...shiftForm, role: e.target.value })} placeholder="Ex: Açougueiro" />
                </div>
                <div className="space-y-1"><Label>Data *</Label>
                  <Input type="date" required value={shiftForm.date} onChange={(e) => setShiftForm({ ...shiftForm, date: e.target.value })} />
                </div>
                <div className="space-y-1"><Label>Turno</Label>
                  <select value={shiftForm.shift} onChange={(e) => setShiftForm({ ...shiftForm, shift: e.target.value })} className="w-full h-10 px-3 border border-slate-200 rounded">
                    <option value="manha">Manhã</option>
                    <option value="tarde">Tarde</option>
                    <option value="noite">Noite</option>
                    <option value="integral">Integral</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div className="space-y-1"><Label>Valor (R$)</Label>
                  <Input type="number" step="0.01" value={shiftForm.daily_rate} onChange={(e) => setShiftForm({ ...shiftForm, daily_rate: e.target.value })} />
                </div>
                <div className="space-y-1"><Label>Pago?</Label>
                  <label className="flex items-center gap-2 h-10">
                    <input type="checkbox" checked={shiftForm.paid} onChange={(e) => setShiftForm({ ...shiftForm, paid: e.target.checked })} />
                    <span className="text-sm">Marca como pago</span>
                  </label>
                </div>
                <div className="md:col-span-6">
                  <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="add-shift-btn">Registrar Diária</Button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Data</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Funcionário</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Função</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Turno</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-wider text-slate-500 font-bold">Valor</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Pago?</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-slate-500 font-bold">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {shifts.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-slate-400">Nenhuma diária</td></tr>}
                  {shifts.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm">{new Date(s.date).toLocaleDateString('pt-BR')}</td>
                      <td className="px-4 py-3">{s.employee_name}</td>
                      <td className="px-4 py-3 text-sm">{s.role || '-'}</td>
                      <td className="px-4 py-3 capitalize">{s.shift}</td>
                      <td className="px-4 py-3 text-right font-bold">R$ {Number(s.daily_rate).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {s.paid
                          ? <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700 font-bold">Pago</span>
                          : <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-700 font-bold">Pendente</span>}
                      </td>
                      <td className="px-4 py-3">
                        {!s.paid && (
                          <button onClick={() => markShiftPaid(s.id)} className="text-green-600 text-sm flex items-center gap-1">
                            <CheckCircle size={14} /> Marcar pago
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};
